import React from 'react';
import { Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faRocket, faCreditCard } from '@fortawesome/free-solid-svg-icons';

interface SimpleBrokeragePricingCardProps {
  agentCount?: number;
  onSubscribeClick?: (subscriptionType: 'per-office' | 'transaction-coordinator') => void;
}

const SimpleBrokeragePricingCard: React.FC<SimpleBrokeragePricingCardProps> = ({
  agentCount = 0,
  onSubscribeClick
}) => {
  const officeFee = 500;
  const perAgentFee = 70;
  const totalMonthly = officeFee + (agentCount * perAgentFee);

  return (
    <div className="mb-4">
      {/* Main Pricing Card */}
      <Card
        className="border-0 mb-4"
        style={{
          overflow: 'visible',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)',
          borderRadius: '20px'
        }}
      >
        <Card.Body className="p-5">
          {/* Badge */}
          <div className="text-center mb-4">
            <Badge
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                padding: '10px 30px',
                fontSize: '13px',
                fontWeight: 'bold',
                letterSpacing: '1px',
                borderRadius: '25px',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                textTransform: 'uppercase'
              }}
            >
              Simple Per-Office Pricing
            </Badge>
          </div>

          {/* Main Price */}
          <div className="text-center mb-3">
            <h1
              className="mb-0"
              style={{
                fontSize: '64px',
                fontWeight: '800',
                color: '#1f2937',
                lineHeight: '1'
              }}
            >
              $500<span style={{ fontSize: '32px', fontWeight: '600' }}>/month</span>
            </h1>
            <p className="mt-2" style={{ fontSize: '18px', color: '#1f2937', fontWeight: '600' }}>
              per office
            </p>
          </div>

          {/* Subheading */}
          <div className="text-center mb-5 pb-4" style={{ borderBottom: '2px solid rgba(16, 185, 129, 0.2)' }}>
            <p
              className="mb-0"
              style={{
                fontSize: '20px',
                color: '#6b7280',
                fontWeight: '500'
              }}
            >
              + $70/month per agent • Unlimited document processing
            </p>
          </div>

          {/* Benefits Grid */}
          <Row className="g-4 mb-4">
            <Col md={6}>
              <div className="d-flex align-items-start">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{
                    color: '#10b981',
                    fontSize: '24px',
                    marginRight: '16px',
                    marginTop: '4px'
                  }}
                />
                <div>
                  <p className="mb-0" style={{ fontSize: '18px', color: '#1f2937', fontWeight: '600' }}>
                    Multiple offices? Just $500 each
                  </p>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="d-flex align-items-start">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{
                    color: '#10b981',
                    fontSize: '24px',
                    marginRight: '16px',
                    marginTop: '4px'
                  }}
                />
                <div>
                  <p className="mb-0" style={{ fontSize: '18px', color: '#1f2937', fontWeight: '600' }}>
                    No per-transaction fees
                  </p>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="d-flex align-items-start">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{
                    color: '#10b981',
                    fontSize: '24px',
                    marginRight: '16px',
                    marginTop: '4px'
                  }}
                />
                <div>
                  <p className="mb-0" style={{ fontSize: '18px', color: '#1f2937', fontWeight: '600' }}>
                    Unlimited AI compliance checks
                  </p>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="d-flex align-items-start">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{
                    color: '#10b981',
                    fontSize: '24px',
                    marginRight: '16px',
                    marginTop: '4px'
                  }}
                />
                <div>
                  <p className="mb-0" style={{ fontSize: '18px', color: '#1f2937', fontWeight: '600' }}>
                    5-year document storage included
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Current Cost Display (if agents exist) */}
          {agentCount > 0 && (
            <div
              className="mt-4 p-4 rounded-3 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                border: '2px solid rgba(16, 185, 129, 0.3)'
              }}
            >
              <div className="mb-2">
                <span style={{ fontSize: '16px', color: '#6b7280', fontWeight: '500' }}>
                  Your Current Monthly Cost
                </span>
              </div>
              <div className="d-flex align-items-center justify-content-center gap-3">
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Office: ${officeFee}
                </span>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>+</span>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  {agentCount} agents × ${perAgentFee}: ${(agentCount * perAgentFee).toLocaleString()}
                </span>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>=</span>
                <span style={{ fontSize: '28px', color: '#10b981', fontWeight: '800' }}>
                  ${totalMonthly.toLocaleString()}/mo
                </span>
              </div>
            </div>
          )}

          {/* Subscribe Button */}
          {onSubscribeClick && (
            <div className="text-center mt-4">
              <Button
                size="lg"
                onClick={() => onSubscribeClick('per-office')}
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  padding: '14px 40px',
                  fontSize: '18px',
                  fontWeight: '600',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                }}
              >
                <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                Subscribe Now
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* AI Transaction Coordinator Card */}
      <Card
        className="border-0"
        style={{
          overflow: 'visible',
          background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.05) 0%, rgba(234, 88, 12, 0.05) 100%)',
          borderRadius: '20px',
          border: '2px solid rgba(249, 115, 22, 0.2)'
        }}
      >
        <Card.Body className="p-5">
          {/* Badge */}
          <div className="text-center mb-4">
            <Badge
              style={{
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                color: 'white',
                padding: '10px 30px',
                fontSize: '13px',
                fontWeight: 'bold',
                letterSpacing: '1px',
                borderRadius: '25px',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                textTransform: 'uppercase'
              }}
            >
              Brokerage-to-Brokerage Service
            </Badge>
          </div>

          {/* Title */}
          <div className="text-center mb-3">
            <h2
              className="mb-3"
              style={{
                fontSize: '42px',
                fontWeight: '800',
                color: '#1f2937'
              }}
            >
              AI Transaction Coordinator
            </h2>
            <p className="mb-2" style={{ fontSize: '16px', color: '#6b7280' }}>
              Provided by KairosIQ AI Realty, Inc. – A licensed brokerage partner
            </p>
            <div className="d-flex align-items-center justify-content-center gap-2 mb-4">
              <FontAwesomeIcon icon={faRocket} style={{ color: '#10b981', fontSize: '20px' }} />
              <span style={{ fontSize: '18px', color: '#10b981', fontWeight: '700' }}>
                Launching February 2026
              </span>
            </div>
          </div>

          <Row className="g-4">
            {/* What's Included */}
            <Col md={6}>
              <Card className="h-100 border-0" style={{ background: 'white', borderRadius: '16px' }}>
                <Card.Body className="p-4">
                  <div
                    className="mb-4 d-flex align-items-center justify-content-center"
                    style={{
                      width: '64px',
                      height: '64px',
                      background: 'linear-gradient(135deg, #f97316, #ea580c)',
                      borderRadius: '16px',
                      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                    }}
                  >
                    <span style={{ fontSize: '32px' }}>🤖</span>
                  </div>

                  <h4 className="mb-4" style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                    What's Included
                  </h4>

                  <ul className="list-unstyled" style={{ fontSize: '16px', color: '#4b5563' }}>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="me-2">•</span>
                      <span>Save 7-9 hours per transaction</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="me-2">•</span>
                      <span>24/7 transaction monitoring</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="me-2">•</span>
                      <span>Automated milestone tracking</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="me-2">•</span>
                      <span>Client communication management</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="me-2">•</span>
                      <span>Compliance checkpoint verification</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="me-2">•</span>
                      <span>Real-time status updates</span>
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* Pricing */}
            <Col md={6}>
              <Card
                className="h-100 border-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 237, 213, 0.6) 0%, rgba(254, 215, 170, 0.6) 100%)',
                  borderRadius: '16px',
                  border: '2px solid rgba(249, 115, 22, 0.3)'
                }}
              >
                <Card.Body className="p-4 d-flex flex-column justify-content-center align-items-center text-center">
                  <h4 className="mb-3" style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937' }}>
                    Simple Transaction-Based Pricing
                  </h4>

                  <div className="mb-4">
                    <h2
                      className="mb-0"
                      style={{
                        fontSize: '56px',
                        fontWeight: '800',
                        color: '#f97316'
                      }}
                    >
                      $300
                    </h2>
                    <span style={{ fontSize: '24px', color: '#6b7280', fontWeight: '600' }}>
                      /transaction
                    </span>
                  </div>

                  <p className="mb-3" style={{ fontSize: '16px', color: '#6b7280' }}>
                    No monthly fees • No minimums • Pay only when you close deals
                  </p>

                  <p className="mb-0" style={{ fontSize: '14px', color: '#9ca3af', fontStyle: 'italic' }}>
                    Service provided under brokerage partnership agreement with KairosIQ AI Realty, Inc.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Subscribe Button for Transaction Coordinator */}
          {onSubscribeClick && (
            <div className="text-center mt-4">
              <Button
                size="lg"
                onClick={() => onSubscribeClick('transaction-coordinator')}
                disabled
                style={{
                  background: 'linear-gradient(135deg, #f97316, #ea580c)',
                  border: 'none',
                  padding: '14px 40px',
                  fontSize: '18px',
                  fontWeight: '600',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.4)',
                  opacity: 0.6
                }}
              >
                <FontAwesomeIcon icon={faRocket} className="me-2" />
                Coming February 2026
              </Button>
              <p className="mt-2 mb-0 text-muted" style={{ fontSize: '14px' }}>
                Pre-order access available for early adopters
              </p>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SimpleBrokeragePricingCard;
